/**
 * The shared handle-driven reorderable list (BL-1479).
 *
 * A vertical list of rows, each with a six-dot grab handle on its left. Drag a
 * handle and the row it belongs to moves; top of the list is first. The list
 * knows nothing about what the rows MEAN — `ColumnsMenu` uses it for table
 * columns, and the next surface that needs reordering consumes it rather than
 * hand-rolling a third implementation (ADR 030's "one job, one implementation").
 *
 * WHY drag binds to the handle and nothing else: the row's content is itself
 * interactive on every consumer we have (the Columns menu's rows are
 * show/hide checkboxes). Putting the drag on the whole row is what forces a
 * press-vs-click disambiguation, and it is exactly the overload that made the
 * retired table-header gesture unusable — that header carried sort, resize AND
 * move on one target, so its drag feedback had to be so timid nobody could feel
 * it. One gesture, one target, one outcome.
 *
 * WHY the hovered row reports itself (a per-row `onMouseMove`) rather than the
 * list hit-testing the cursor with `document.elementFromPoint`: the rows already
 * know their own identity, so asking them is both cheaper and — the reason that
 * matters here — actually testable, since jsdom's `elementFromPoint` returns
 * nothing useful. Same technique the retired `useColumnReorder` used, kept.
 *
 * WHY the source row stays in place, dimmed, while a separate ghost rides the
 * cursor: it is the approved interaction (Dan, 2026-08-30). Keeping the row in
 * place means the list never reflows mid-drag, so the insert line is the only
 * thing that moves and the operator has one thing to aim at.
 *
 * WHY the ghost tracks only the cursor's Y: pinned to the row's own left edge it
 * reads as the row sliding up and down its own list. Tracking X as well makes it
 * swing away from the list and stop looking like the thing being reordered.
 *
 * WHY ArrowUp/ArrowDown live here rather than in a separate control: a pointer
 * drag is unreachable by keyboard and by screen reader, and the previous answer
 * to that — a pair of `<` / `>` buttons beside every row — was a SECOND
 * affordance with its own semantics to learn. The handle is one control that
 * both gestures drive, and the aria-live announcement is what makes the keyboard
 * path self-describing.
 *
 * The list is CONTROLLED: it never reorders `items` itself, it reports the move
 * and the consumer applies it. That is what lets the consumer's own model own
 * persistence, validation and pinning (the Columns menu's pinned columns never
 * reach this component at all).
 */
'use client';

import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Pixels of vertical travel before a press becomes a drag.
 *
 * Small enough to feel immediate, large enough that a shaky click on the handle
 * is still a click. Matches the approved mock.
 */
const DRAG_THRESHOLD_PX = 4;

/** Which side of the hovered row a drop lands on. */
export type ReorderDropPosition = 'before' | 'after';

export interface ReorderableListItem {
    /** Stable identity — also the React key, which is what preserves handle focus across a move. */
    key: string;
    /** Plain-text name, used for the handle's accessible name and the move announcement. */
    label: string;
    /** Whatever the row renders beside its handle. */
    content: ReactNode;
}

/**
 * Per-app appearance binding — class names only, no structure (ADR 030 § 2).
 *
 * Every slot is optional so a consumer that hasn't declared a dialect still gets
 * the working, accessible structure.
 */
export interface ReorderableListDialect {
    /** The list wrapper. */
    root?: string;
    /** Each row. */
    row?: string;
    /** The grab handle button. */
    handle?: string;
    /** The floating copy that rides the cursor. */
    ghost?: string;
    /** The insert line showing where the drop lands. */
    dropLine?: string;
}

export interface ReorderableListProps {
    items: readonly ReorderableListItem[];
    /**
     * Report a move. The list does NOT apply it — the consumer owns the order.
     *
     * @sideEffect None here; the consumer's handler is what persists.
     */
    onMove: (key: string, targetKey: string, position: ReorderDropPosition) => void;
    /** Override the handle's accessible name. */
    ariaLabelFor?: (label: string) => string;
    /** Override the move announcement. */
    announce?: (label: string, position: number, total: number) => string;
    dialect?: ReorderableListDialect;
    /** Extra classes on the wrapper. */
    className?: string;
    /**
     * ARIA role for the handle button, when the list is rendered inside a
     * container that constrains what its descendants may be.
     *
     * The Columns dropdown is a `role="menu"`, whose children must be
     * `menuitem*` — so it passes `"menuitem"` here, exactly as the `<` / `>`
     * controls this replaced did. A standalone list passes nothing and the
     * handle stays a plain button. Structural, not a domain value: it names the
     * surface the list is on, never anything about the rows.
     */
    handleRole?: string;
}

const defaultAriaLabel = (label: string) =>
    `Reorder ${label}. Use arrow up and arrow down to move.`;

const defaultAnnounce = (label: string, position: number, total: number) =>
    `${label} moved to position ${position} of ${total}`;

/** The six-dot grab glyph. Two columns of three, matching the approved mock. */
function GrabDots() {
    return (
        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">
            <circle cx="2.6" cy="3" r="1.35" />
            <circle cx="7.4" cy="3" r="1.35" />
            <circle cx="2.6" cy="8" r="1.35" />
            <circle cx="7.4" cy="8" r="1.35" />
            <circle cx="2.6" cy="13" r="1.35" />
            <circle cx="7.4" cy="13" r="1.35" />
        </svg>
    );
}

/** A live press, held in a ref so a mousemove doesn't re-render on every pixel. */
interface PressState {
    key: string;
    startY: number;
    engaged: boolean;
    left: number;
}

/**
 * Announce a completed move once the consumer has applied it.
 *
 * The announcement has to name the row's NEW position, which only exists after
 * `items` comes back reordered — so it is keyed on the order actually changing
 * rather than on the move being reported. A consumer that ignores `onMove`
 * announces nothing, which is correct: nothing moved.
 */
function useMoveAnnouncement(
    items: readonly ReorderableListItem[],
    announce: (label: string, position: number, total: number) => string,
) {
    const previousKeys = useRef<readonly string[] | null>(null);
    const pendingKey = useRef<string | null>(null);
    const [message, setMessage] = useState('');

    useEffect(() => {
        // The order is compared element-wise, and the key list is derived HERE
        // rather than in the render body for two reasons: a list built during
        // render is a new array every time, so it could not be an effect
        // dependency without re-running this on every render; and joining the
        // keys into one comparable string needs a separator, where the only
        // character a key genuinely cannot contain is a NUL — which, written
        // into a source file, is a literal NUL byte that makes git classify the
        // whole component as BINARY. (That is not hypothetical: this file
        // shipped that way for one commit, with no reviewable diff.)
        const keys = items.map((item) => item.key);
        const previous = previousKeys.current;
        previousKeys.current = keys;

        const unchanged =
            previous !== null &&
            previous.length === keys.length &&
            previous.every((key, i) => key === keys[i]);
        if (unchanged || previous === null) return;

        const key = pendingKey.current;
        pendingKey.current = null;
        if (!key) return;

        const index = items.findIndex((item) => item.key === key);
        if (index < 0) return;
        setMessage(announce(items[index].label, index + 1, items.length));
    }, [items, announce]);

    return { message, pendingKey };
}

export default function ReorderableList({
    items,
    onMove,
    ariaLabelFor = defaultAriaLabel,
    announce = defaultAnnounce,
    dialect,
    className,
    handleRole,
}: ReorderableListProps) {
    const [draggingKey, setDraggingKey] = useState<string | null>(null);
    const [ghost, setGhost] = useState<{ left: number; top: number } | null>(null);
    const [drop, setDrop] = useState<{ key: string; position: ReorderDropPosition } | null>(null);
    const [pressing, setPressing] = useState(false);

    const pressRef = useRef<PressState | null>(null);
    // Mirrors `drop` so the mouseup listener reads the current target rather than
    // the value captured when the listener was attached.
    const dropRef = useRef<typeof drop>(null);
    const { message, pendingKey } = useMoveAnnouncement(items, announce);

    const setDropTarget = useCallback((next: typeof drop) => {
        dropRef.current = next;
        setDrop(next);
    }, []);

    const reset = useCallback(() => {
        pressRef.current = null;
        setPressing(false);
        setDraggingKey(null);
        setGhost(null);
        dropRef.current = null;
        setDrop(null);
    }, []);

    const report = useCallback(
        (key: string, targetKey: string, position: ReorderDropPosition) => {
            pendingKey.current = key;
            onMove(key, targetKey, position);
        },
        [onMove, pendingKey],
    );

    // Window-level listeners live only while a press is open, so an idle list
    // costs nothing and a drag can't be lost by releasing outside the list.
    useEffect(() => {
        if (!pressing) return;

        const handleMove = (event: MouseEvent) => {
            const press = pressRef.current;
            if (!press) return;
            if (!press.engaged) {
                if (Math.abs(event.clientY - press.startY) < DRAG_THRESHOLD_PX) return;
                press.engaged = true;
                setDraggingKey(press.key);
            }
            setGhost({ left: press.left, top: event.clientY });
        };

        const handleUp = () => {
            const press = pressRef.current;
            const target = dropRef.current;
            reset();
            if (press?.engaged && target && target.key !== press.key) {
                report(press.key, target.key, target.position);
            }
        };

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [pressing, reset, report]);

    const startPress = useCallback((key: string, event: React.MouseEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;
        // Stops the press from selecting the row's text mid-drag — but
        // preventDefault on mousedown ALSO suppresses the browser's own
        // focus-the-button behaviour, so focus has to be taken explicitly.
        // Without this, clicking a handle and then pressing an arrow key does
        // nothing, and focus is not on the handle after a drop either.
        event.preventDefault();
        event.currentTarget.focus();
        const row = event.currentTarget.closest('[data-reorderable-row]');
        pressRef.current = {
            key,
            startY: event.clientY,
            engaged: false,
            left: row ? row.getBoundingClientRect().left : 0,
        };
        setPressing(true);
    }, []);

    const trackHover = useCallback(
        (key: string, event: React.MouseEvent<HTMLDivElement>) => {
            const press = pressRef.current;
            if (!press?.engaged) return;
            if (key === press.key) {
                setDropTarget(null);
                return;
            }
            const box = event.currentTarget.getBoundingClientRect();
            setDropTarget({
                key,
                position: event.clientY < box.top + box.height / 2 ? 'before' : 'after',
            });
        },
        [setDropTarget],
    );

    const moveByKeyboard = useCallback(
        (key: string, event: React.KeyboardEvent<HTMLButtonElement>) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
            event.preventDefault();
            const index = items.findIndex((item) => item.key === key);
            if (event.key === 'ArrowUp' && index > 0) {
                report(key, items[index - 1].key, 'before');
            }
            if (event.key === 'ArrowDown' && index >= 0 && index < items.length - 1) {
                report(key, items[index + 1].key, 'after');
            }
        },
        [items, report],
    );

    const draggingLabel = useMemo(
        () => items.find((item) => item.key === draggingKey)?.label ?? '',
        [items, draggingKey],
    );

    return (
        <div
            data-testid="reorderable-list"
            // The rows only ever SET the target, so without this the last row
            // hovered stays armed after the pointer leaves the list and a
            // release anywhere on the page commits a move to it (BUG-1925).
            // Leaving clears the target but not the drag: the window listeners
            // still own the release, so a drop out here reports nothing, and
            // coming back over a row picks a target up again.
            onMouseLeave={() => setDropTarget(null)}
            className={clsx(dialect?.root, className)}
        >
            {items.map((item) => (
                <div
                    key={item.key}
                    // A row is a wrapper, never a control — the handle and the
                    // content are its siblings-in-spirit, and marking it
                    // presentational keeps it out of the accessibility tree of
                    // whatever container the list is rendered into.
                    role="none"
                    data-reorderable-row
                    data-testid={`reorderable-row-${item.key}`}
                    data-dragging={draggingKey === item.key ? 'true' : undefined}
                    data-drop={drop?.key === item.key ? drop.position : undefined}
                    onMouseMove={(event) => trackHover(item.key, event)}
                    // The dim is part of the GESTURE, not the app's appearance:
                    // it is what says "this row is the thing in hand" while the
                    // list itself holds still. So it lives here rather than in a
                    // dialect, and it is an inline style because it is a dynamic
                    // per-render value (`ui-standards.md`'s carve-out) — a class
                    // would need every consumer's Tailwind build to have
                    // generated the matching `data-[dragging]` variant.
                    style={draggingKey === item.key ? { opacity: 0.4 } : undefined}
                    className={clsx('relative flex items-center', dialect?.row)}
                >
                    <button
                        type="button"
                        role={handleRole}
                        aria-label={ariaLabelFor(item.label)}
                        title="Drag to reorder"
                        onMouseDown={(event) => startPress(item.key, event)}
                        onKeyDown={(event) => moveByKeyboard(item.key, event)}
                        className={clsx('shrink-0 cursor-grab', dialect?.handle)}
                    >
                        <GrabDots />
                    </button>
                    {item.content}
                    {drop?.key === item.key && (
                        <span
                            aria-hidden="true"
                            data-testid="reorderable-drop-line"
                            style={{
                                position: 'absolute',
                                left: 8,
                                right: 8,
                                height: 2,
                                pointerEvents: 'none',
                                ...(drop.position === 'before' ? { top: -1 } : { bottom: -1 }),
                            }}
                            className={clsx('rounded-sm bg-primary', dialect?.dropLine)}
                        />
                    )}
                </div>
            ))}

            {ghost && draggingKey && (
                <div
                    aria-hidden="true"
                    data-testid="reorderable-ghost"
                    style={{ position: 'fixed', left: ghost.left, top: ghost.top, zIndex: 60 }}
                    className={clsx(
                        // The slight rotation is the approved design's — it is
                        // what stops the ghost reading as a row that merely
                        // detached, and makes it read as one picked up.
                        'pointer-events-none flex -translate-y-1/2 -rotate-1 items-center gap-1 rounded-lg bg-white px-2 py-1 text-sm shadow-lg',
                        dialect?.ghost,
                    )}
                >
                    <span className="text-gray-500">
                        <GrabDots />
                    </span>
                    <span>{draggingLabel}</span>
                </div>
            )}

            {/* One announcement channel for BOTH gestures, so a keyboard move and
                a pointer drop report the same thing to assistive tech. */}
            <span role="status" aria-live="polite" className="sr-only">
                {message}
            </span>
        </div>
    );
}
